// Picks the site's look from the visitor's sky: day, twilight or night.
// Night follows the FAA definition — after the end of evening civil
// twilight (sun 6° below the horizon) and before the start of morning civil
// twilight.

const RAD = Math.PI / 180;

// Low-precision solar position (Astronomical Almanac); good to a fraction of
// a degree, which is plenty for choosing a colour scheme.
export function solarElevation(date, lat, lon) {
  const n = date.getTime() / 86400000 + 2440587.5 - 2451545.0; // days since J2000
  const meanLon = 280.46 + 0.9856474 * n;
  const anomaly = (357.528 + 0.9856003 * n) * RAD;
  const eclipticLon = (meanLon + 1.915 * Math.sin(anomaly) + 0.02 * Math.sin(2 * anomaly)) * RAD;
  const obliquity = (23.439 - 0.0000004 * n) * RAD;
  const declination = Math.asin(Math.sin(obliquity) * Math.sin(eclipticLon));
  const rightAscension = Math.atan2(Math.cos(obliquity) * Math.sin(eclipticLon), Math.cos(eclipticLon));
  const siderealHours = 18.697374558 + 24.06570982441908 * n;
  const hourAngle = (siderealHours * 15 + lon) * RAD - rightAscension;
  const sinElevation =
    Math.sin(lat * RAD) * Math.sin(declination) + Math.cos(lat * RAD) * Math.cos(declination) * Math.cos(hourAngle);
  return Math.asin(sinElevation) / RAD;
}

export function phaseOfDay(elevation) {
  if (elevation <= -6) return 'night';
  if (elevation < 6) return 'dawn'; // civil twilight and golden hour, morning or evening
  return 'day';
}

// Representative coordinates for common time zones. Anything else falls back
// to a longitude from the UTC offset and a latitude from the zone's region.
const ZONES = {
  'America/Los_Angeles': [34.05, -118.25],
  'America/Vancouver': [49.28, -123.12],
  'America/Phoenix': [33.45, -112.07],
  'America/Denver': [39.74, -104.99],
  'America/Chicago': [41.88, -87.63],
  'America/Mexico_City': [19.43, -99.13],
  'America/New_York': [40.71, -74.01],
  'America/Toronto': [43.65, -79.38],
  'America/Halifax': [44.65, -63.57],
  'America/Anchorage': [61.22, -149.9],
  'America/Sao_Paulo': [-23.55, -46.63],
  'America/Argentina/Buenos_Aires': [-34.6, -58.38],
  'America/Santiago': [-33.45, -70.67],
  'America/Bogota': [4.71, -74.07],
  'America/Lima': [-12.05, -77.04],
  'Pacific/Honolulu': [21.31, -157.86],
  'Pacific/Auckland': [-36.85, 174.76],
  'Europe/London': [51.51, -0.13],
  'Europe/Dublin': [53.35, -6.26],
  'Europe/Lisbon': [38.72, -9.14],
  'Europe/Madrid': [40.42, -3.7],
  'Europe/Paris': [48.86, 2.35],
  'Europe/Amsterdam': [52.37, 4.9],
  'Europe/Berlin': [52.52, 13.4],
  'Europe/Zurich': [47.38, 8.54],
  'Europe/Rome': [41.9, 12.5],
  'Europe/Stockholm': [59.33, 18.07],
  'Europe/Warsaw': [52.23, 21.01],
  'Europe/Athens': [37.98, 23.73],
  'Europe/Istanbul': [41.01, 28.98],
  'Europe/Kyiv': [50.45, 30.52],
  'Europe/Moscow': [55.76, 37.62],
  'Asia/Dubai': [25.2, 55.27],
  'Asia/Kolkata': [28.61, 77.21],
  'Asia/Singapore': [1.35, 103.82],
  'Asia/Hong_Kong': [22.32, 114.17],
  'Asia/Shanghai': [31.23, 121.47],
  'Asia/Seoul': [37.57, 126.98],
  'Asia/Tokyo': [35.68, 139.69],
  'Australia/Perth': [-31.95, 115.86],
  'Australia/Sydney': [-33.87, 151.21],
  'Africa/Johannesburg': [-26.2, 28.05],
  'Africa/Lagos': [6.52, 3.38],
  'Africa/Cairo': [30.04, 31.24],
};

const REGION_LAT = { America: 38, Europe: 50, Asia: 30, Africa: 5, Australia: -30, Pacific: -15, Atlantic: 40, Indian: -10 };

export function guessLocation(timeZone, now = new Date()) {
  if (ZONES[timeZone]) return ZONES[timeZone];
  // Standard (non-DST) offset: the larger of January's and July's.
  const year = now.getFullYear();
  const standard = Math.max(new Date(year, 0, 1).getTimezoneOffset(), new Date(year, 6, 1).getTimezoneOffset());
  const lat = REGION_LAT[(timeZone || '').split('/')[0]] ?? 35;
  return [lat, -standard / 4];
}

export function localPhase(now = new Date()) {
  let timeZone;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    timeZone = undefined;
  }
  const [lat, lon] = guessLocation(timeZone, now);
  return phaseOfDay(solarElevation(now, lat, lon));
}
